import { useState } from 'react';
import { Box, Button, Checkbox, Chip, Divider, IconButton } from '@mui/material';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { MatrixCriterion, MatrixCriterionValue, MatrixFeatureHome, NewMatrixCriterion } from '../../../../types/advise';
import { criterionCount } from './FeatureMatrixPanel.calc';
import { selectionValue } from './CriteriaWeighting';
import { AddCustomCategoryDialog } from './AddCustomCategoryDialog';
import { ConfirmDialog } from '../ConfirmDialog';
import { useConfirmedRemoval } from '../useConfirmedRemoval';
import { MATRIX_ADD_BUTTON } from './matrixAddButton';
import { MatrixCategoryBlock } from './MatrixCategoryBlock';
import { NoChecklistCallout } from './NoChecklistCallout';

export interface DetailedFunctionalityMatrixProps {
  criteria: MatrixCriterion[];
  canEdit: boolean;
  onChangeCriterion: (criterionId: string, value: MatrixCriterionValue) => void;
  onSelectFeature: (featureId: string, selected: boolean) => void;
  onAddCriterion?: (criterion: NewMatrixCriterion) => Promise<string | null>;
  onAddFeature?: (home: MatrixFeatureHome, label: string) => Promise<string | null>;
  onRemoveCriterion?: (criterionId: string) => Promise<string | null>;
  onRemoveFeature?: (featureId: string) => Promise<string | null>;
  onBack: () => void;
}

export const DetailedFunctionalityMatrix = ({
  criteria,
  canEdit,
  onChangeCriterion,
  onSelectFeature,
  onAddCriterion,
  onAddFeature,
  onRemoveCriterion,
  onRemoveFeature,
  onBack
}: DetailedFunctionalityMatrixProps) => {
  const [addingCriterion, setAddingCriterion] = useState(false);
  const criterionRemoval = useConfirmedRemoval<MatrixCriterion>(async (criterion) =>
    onRemoveCriterion ? onRemoveCriterion(criterion.id) : null
  );

  return (
    <Box data-testid="detailed-functionality-matrix">
      <Box className="flex flex-wrap items-center justify-between gap-2 py-3">
        <TextSpan
          size="sm14"
          color="grey6">
          Select the features required for this engagement, grouped by evaluation criterion. Selections update the
          Feature Matrix comparison and supplier scoring automatically.
        </TextSpan>
        <Button
          size="small"
          onClick={onBack}>
          ← Functionality Matrix overview
        </Button>
      </Box>
      {canEdit && onAddCriterion && (
        <Box className="flex justify-end mb-2">
          <Button
            size="small"
            {...MATRIX_ADD_BUTTON}
            onClick={() => setAddingCriterion(true)}>
            + Add Custom Category
          </Button>
          {addingCriterion && (
            <AddCustomCategoryDialog
              onClose={() => setAddingCriterion(false)}
              onCreate={onAddCriterion}
            />
          )}
        </Box>
      )}
      <Divider />
      {criteria.map((criterion) => {
        const count = criterionCount(criterion);
        const noFeatures = count.total + count.custom === 0;
        return (
          <Box
            key={criterion.id}
            className="px-6 py-4"
            data-testid={`matrix-criterion-${criterion.key}`}>
            <Box className="flex items-center gap-2">
              <Checkbox
                size="small"
                checked={criterion.selected}
                disabled={!canEdit}
                onChange={(event) => onChangeCriterion(criterion.id, selectionValue(event.target.checked))}
                inputProps={{ 'aria-label': criterion.label }}
              />
              <TextSpan
                size="sm14"
                weight="semibold"
                color={criterion.selected ? 'grey8' : 'grey5'}
                className="flex-1">
                {criterion.label}
                {criterion.custom && (
                  <Chip
                    size="small"
                    label="CUSTOM CRITERION"
                    color="primary"
                    variant="outlined"
                    className="ml-2"
                  />
                )}
                {criterion.custom && canEdit && onRemoveCriterion && (
                  <IconButton
                    size="small"
                    aria-label={`Remove ${criterion.label}`}
                    className="ml-1"
                    onClick={() => criterionRemoval.ask(criterion)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </TextSpan>
              <TextSpan
                size="sm14"
                weight="medium"
                color={noFeatures ? 'orange' : 'indigo'}>
                {noFeatures ? 'No features mapped' : `${count.selected} of ${count.total} selected`}
              </TextSpan>
            </Box>
            <TextBlock
              size="sm14"
              color="grey6"
              className="ml-10">
              {criterion.description ||
                (criterion.custom ? 'Custom criterion — set its weight on the Criteria Weighting step' : null)}
            </TextBlock>
            <Box className="ml-10">
              {criterion.categories.length === 0 ? (
                <NoChecklistCallout
                  criterion={criterion}
                  canEdit={canEdit}
                  onAddFeature={onAddFeature}
                />
              ) : (
                criterion.categories.map((category) => (
                  <MatrixCategoryBlock
                    key={category.id}
                    category={category}
                    canEdit={canEdit}
                    onSelectFeature={onSelectFeature}
                    onAddFeature={onAddFeature}
                    onRemoveFeature={onRemoveFeature}
                  />
                ))
              )}
            </Box>
            <Divider className="mt-4" />
          </Box>
        );
      })}
      <ConfirmDialog
        open={criterionRemoval.open}
        title={`Remove ${criterionRemoval.target?.label ?? ''}?`}
        message="This removes the custom criterion, its category and its features from this engagement."
        confirmLabel="Remove"
        refusal={criterionRemoval.refusal}
        onConfirm={criterionRemoval.confirm}
        onCancel={criterionRemoval.cancel}
      />
    </Box>
  );
};
